/**
 * « Recommencer » sur la carte d'un élève : le formulaire de cadrage de « Générer une activité »
 * (`<TutorGenerateActivityFlow>`, étape `form` seule), dont la validation repart vers la
 * régénération du champion au lieu de créer des activités — `RegenerateModal.js`, qui montait
 * `GenerateActivityModal` en simple formulaire (`onSubmitOverride`) : ni dépôt de fichier, ni trois
 * énoncés, la feuille se ferme à la validation.
 *
 * Le formulaire s'ouvre sur le chapitre retenu par le champion, le type « Entraînement » et la durée de la
 * classe ; ne part que ce que le masteur a changé (`regenerateOptions`, port de
 * `buildRegeneratePayload`). Un élève en programme de révision a l'interrupteur du mode programme,
 * éteint à l'ouverture comme le legacy ; allumé, le chapitre se choisit dans ses programmes
 * (`getStudyProgramsAsTutor`, la feuille du programme).
 *
 * Le crochet ne régénère pas : il rend les options à `onSubmit`, et l'écran décide (la boîte
 * « Remplacer les exercices proposés ? », la mesure, l'envoi).
 */
import { useCallback, useEffect, useMemo, useReducer } from "react"

import { chaptersService } from "../../../../../../data/services/chaptersService"
import {
    ACTIVITY_DURATION_OPTIONS,
    ACTIVITY_LEVEL_OPTIONS,
    defaultMaterialDuration,
    MIN_INSTRUCTIONS_LENGTH,
} from "../../../../adapters/parent/Activities/activityGenerationAdapter"
import {
    toUISectionedChapters,
    type ApiCatalogChapter,
} from "../../../../adapters/parent/SchoolAssessments/sectionedChapterAdapter"
import { regenerateOptions, type RegenerateScope } from "../../../../adapters/tutor/Lives/tutorPrescriptionAdapter"
import {
    type UIActivityGenerationDraft,
    type UIActivityGenerationField,
    type UIActivityGenerationMissingField,
} from "../../../types"
import { type UITutorProgramChapter } from "../../../types/tutorEleves"
import { useApiFetch } from "../../../hooks/useApiFetch"
import { useTutorProgramPicker } from "./useTutorProgramPicker"

type Id = string | number

/** Le formulaire réduit du legacy : ni classe ni matière (celles de l'élève), ni compétences. */
const REGENERATE_FIELDS: UIActivityGenerationField[] = ["chapter", "level", "duration", "instructions"]

/** Le type pré-rempli du formulaire legacy (`selectedLevel: "2"`). */
const DEFAULT_LEVEL = "2"

type FormState = {
    studentId: Id | null
    scope: RegenerateScope | null
    draft: UIActivityGenerationDraft
    program: { enabled: boolean; chapter: UITutorProgramChapter | null }
    sheet: { open: boolean }
}

const CLOSED: FormState = {
    studentId: null,
    scope: null,
    draft: {
        className: null,
        subjectId: null,
        chapterId: null,
        skillPartPositions: [],
        level: DEFAULT_LEVEL,
        duration: "5",
        instructions: "",
        personalizeWithInterests: false,
    },
    program: { enabled: false, chapter: null },
    sheet: { open: false },
}

type FormAction =
    | { type: "OPEN"; studentId: Id; scope: RegenerateScope }
    | { type: "CHANGE"; draft: UIActivityGenerationDraft }
    | { type: "TOGGLE_PROGRAM"; enabled: boolean }
    | { type: "OPEN_SHEET" }
    | { type: "PICK_CHAPTER"; chapter: UITutorProgramChapter }
    | { type: "CLOSE_SHEET" }
    | { type: "CLOSE" }

/** Le brouillon d'ouverture : ce que le formulaire legacy pré-remplissait. */
function openingDraft(scope: RegenerateScope): UIActivityGenerationDraft {
    return {
        ...CLOSED.draft,
        className: scope.className,
        subjectId: scope.subjectId,
        chapterId: scope.chapterId,
        duration: defaultMaterialDuration(scope.className),
    }
}

export function regenerateFormReducer(state: FormState, action: FormAction): FormState {
    switch (action.type) {
        case "OPEN":
            return { ...CLOSED, studentId: action.studentId, scope: action.scope, draft: openingDraft(action.scope) }
        case "CHANGE":
            return { ...state, draft: action.draft }
        // Entrer dans le mode programme efface le chapitre choisi à la main ; en sortir, celui du
        // programme (`handleSetUseStudyProgram`).
        case "TOGGLE_PROGRAM":
            if (action.enabled === state.program.enabled) return state
            return action.enabled
                ? { ...state, draft: { ...state.draft, chapterId: null }, program: { enabled: true, chapter: null } }
                : { ...state, program: { enabled: false, chapter: null } }
        case "OPEN_SHEET":
            return { ...state, sheet: { open: true } }
        case "PICK_CHAPTER":
            return { ...state, program: { ...state.program, chapter: action.chapter }, sheet: { open: false } }
        case "CLOSE_SHEET":
            return { ...state, sheet: { open: false } }
        case "CLOSE":
            return CLOSED
        default:
            return state
    }
}

/** `isGenerationFormValid` du legacy : en programme, son chapitre ; sinon un chapitre ou des instructions. */
export function regenerateMissingField(state: FormState): UIActivityGenerationMissingField | null {
    const { draft, program } = state
    if (program.enabled) return program.chapter != null ? null : "chapter"
    if (!draft.className) return "class"
    if (draft.subjectId == null) return "subject"
    if (draft.chapterId != null || draft.instructions.length >= MIN_INSTRUCTIONS_LENGTH) return null
    return "chapter"
}

/** Ce que la validation envoie : seulement ce qui diffère du pré-remplissage (le « dirty » du formulaire legacy). */
export function regenerateOptionsOf(state: FormState): Record<string, number | string> {
    const opening = state.scope != null ? openingDraft(state.scope) : CLOSED.draft
    const { draft, program } = state
    return regenerateOptions({
        draft,
        useStudyProgram: program.enabled,
        studyProgramChapterId: program.chapter?.id ?? null,
        touched: {
            chapter: program.enabled
                ? program.chapter != null
                : String(draft.chapterId ?? "") !== String(opening.chapterId ?? ""),
            level: draft.level !== opening.level,
            duration: draft.duration !== opening.duration,
        },
    })
}

type ChaptersPayload = { chapters?: ApiCatalogChapter[] | null } | null

export function useTutorPrescriptionRegenerate(args: {
    onSubmit: (studentId: Id, options: Record<string, number | string>) => void
}) {
    const { onSubmit } = args
    const [state, dispatchForm] = useReducer(regenerateFormReducer, CLOSED)
    const { studentId, draft, program, sheet } = state
    const open = studentId != null

    // ── Les chapitres de la classe et de la matière de l'élève (`getChaptersAsTutor`, 100 au plus) ──
    const chapters = useApiFetch<ChaptersPayload>({
        service: chaptersService.getChaptersAsTutor,
        routeParams: { id: studentId },
        fetchOnMount: false,
        scopeUrlParams: ["class", "school_subject_id"],
    })
    const manualChapters = open && !program.enabled && !!draft.className && draft.subjectId != null
    const { fetchWith: fetchChapters, cancel: cancelChapters } = chapters
    const chaptersQuery = manualChapters
        ? { school_subject_id: draft.subjectId, class: draft.className, page_size: 100 }
        : null
    const chaptersKey = chaptersQuery != null ? `${studentId}|${draft.className}|${draft.subjectId}` : null
    useEffect(() => {
        if (chaptersQuery != null) fetchChapters(chaptersQuery)
        // La clé résume la demande : l'objet change à chaque rendu.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chaptersKey, fetchChapters])

    // ── Les programmes de l'élève, pour la feuille du mode programme ──
    const programs = useTutorProgramPicker(program.chapter?.id)
    const { cancel: cancelPrograms } = programs

    const openFor = useCallback((id: Id, scope: RegenerateScope) => {
        dispatchForm({ type: "OPEN", studentId: id, scope })
    }, [])

    const close = useCallback(() => {
        cancelChapters()
        cancelPrograms()
        dispatchForm({ type: "CLOSE" })
    }, [cancelChapters, cancelPrograms])

    const missingField = regenerateMissingField(state)

    const submit = useCallback(() => {
        if (studentId == null || regenerateMissingField(state) != null) return
        const options = regenerateOptionsOf(state)
        dispatchForm({ type: "CLOSE" })
        onSubmit(studentId, options)
    }, [state, studentId, onSubmit])

    const chaptersRaw = chapters.raw
    const catalogs = useMemo(
        () => ({
            classes: [],
            subjects: [],
            chapters: toUISectionedChapters(chaptersRaw?.chapters),
            levels: ACTIVITY_LEVEL_OPTIONS,
            durations: ACTIVITY_DURATION_OPTIONS,
        }),
        [chaptersRaw]
    )

    const studyProgram =
        state.scope?.hasStudyProgram === true
            ? {
                  enabled: program.enabled,
                  onToggle: (next: boolean) => dispatchForm({ type: "TOGGLE_PROGRAM", enabled: next }),
                  chapter:
                      program.chapter != null
                          ? { name: program.chapter.name, parentName: program.chapter.parentName ?? undefined }
                          : null,
                  onPickChapter: () => {
                      if (studentId == null) return
                      dispatchForm({ type: "OPEN_SHEET" })
                      programs.load(studentId)
                  },
              }
            : undefined

    return {
        /** Ouvre le formulaire ; l'écran mesure l'ouverture sur sa propre écriture (`prescription.regenerate`). */
        openFor,
        flow: {
            // La feuille du programme se pose À LA PLACE du formulaire : deux feuilles se disputeraient le portail.
            visible: open && !sheet.open,
            onClose: close,
            step: "form" as const,
            draft,
            onDraftChange: (next: UIActivityGenerationDraft) => dispatchForm({ type: "CHANGE", draft: next }),
            catalogs,
            chaptersLoading: manualChapters && chapters.loading,
            chaptersFailed: manualChapters && chapters.error != null,
            onRetryChapters: () => chaptersQuery != null && fetchChapters(chaptersQuery),
            chapterOrInstructions: true,
            missingField,
            onGenerate: submit,
            allowFile: false,
            formFields: REGENERATE_FIELDS,
            studyProgram,
        },
        programSheet: {
            visible: open && sheet.open,
            onClose: () => {
                cancelPrograms()
                dispatchForm({ type: "CLOSE_SHEET" })
            },
            mode: "pick" as const,
            programs: programs.programs,
            selectedProgramId: programs.selectedProgramId,
            onSelectProgram: programs.onSelectProgram,
            onPickChapter: (chapter: UITutorProgramChapter) => dispatchForm({ type: "PICK_CHAPTER", chapter }),
            loading: programs.loading,
            error: programs.error,
            onRetry: programs.onRetry,
        },
    }
}
